import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { ArticleCard } from '../components/ArticleCard';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Search as SearchIcon, X, SlidersHorizontal, Tag, FileText, ArrowUpDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API, authFetch } from '../lib/api';

export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'relevance');
  const [showFilters, setShowFilters] = useState(false);
  const [popularTags, setPopularTags] = useState([]);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const q = searchParams.get('q');
    const tag = searchParams.get('tag') || '';
    const sort = searchParams.get('sort') || 'relevance';
    if (q) {
      setQuery(q);
      setSelectedTag(tag);
      setSortBy(sort);
      searchArticles(q, tag, sort);
    }
    fetchPopularTags();
  }, [searchParams]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchPopularTags = async () => {
    try {
      const res = await authFetch(`${API}/tags?limit=10`);
      if (res.ok) setPopularTags(await res.json());
    } catch (e) {
      console.error('Failed to fetch popular tags:', e);
    }
  };

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 1) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await authFetch(`${API}/search/suggestions?q=${encodeURIComponent(q)}&limit=6`);
      if (res.ok) setSuggestions(await res.json());
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowSuggestions(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const searchArticles = async (searchQuery, tag = '', sort = 'relevance') => {
    if (!searchQuery || searchQuery.length < 2) return;
    setLoading(true);
    try {
      let url = `${API}/search?q=${encodeURIComponent(searchQuery)}&limit=50&sort=${sort}`;
      if (tag) url += `&tag=${encodeURIComponent(tag)}`;
      const response = await authFetch(url);
      if (response.ok) setArticles(await response.json());
    } catch (error) {
      console.error('Search failed:', error);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      const params = { q: query.trim() };
      if (selectedTag) params.tag = selectedTag;
      if (sortBy !== 'relevance') params.sort = sortBy;
      setSearchParams(params);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    if (suggestion.type === 'article') {
      navigate(`/article/${suggestion.article_id}`);
    } else {
      setQuery(suggestion.text);
      setSearchParams({ q: suggestion.text });
    }
  };

  const handleTagFilter = (tag) => {
    const newTag = tag === selectedTag ? '' : tag;
    setSelectedTag(newTag);
    if (query.trim()) {
      const params = { q: query.trim() };
      if (newTag) params.tag = newTag;
      if (sortBy !== 'relevance') params.sort = sortBy;
      setSearchParams(params);
    }
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
    if (query.trim()) {
      const params = { q: query.trim() };
      if (selectedTag) params.tag = selectedTag;
      if (sort !== 'relevance') params.sort = sort;
      setSearchParams(params);
    }
  };

  const handleBookmarkChange = (articleId, isBookmarked) => {
    setArticles(prev => prev.map(a =>
      a.article_id === articleId ? { ...a, is_bookmarked: isBookmarked } : a
    ));
  };

  const highlightMatch = (text, q) => {
    if (!text || !q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-accent/20 text-foreground rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const activeQuery = searchParams.get('q');

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="search-page">
      <Helmet>
        <title>{`${activeQuery ? `"${activeQuery}" — Search` : 'Search'} | The Blogs Grid`}</title>
        <meta name="description" content={activeQuery ? `Search results for "${activeQuery}" on The Blogs Grid` : 'Search articles on The Blogs Grid'} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Search Header */}
        <div className="mb-8 fade-up">
          <h1 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
            Search
          </h1>

          <form onSubmit={handleSearch} className="relative" data-testid="search-form">
            <div className="relative">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onFocus={() => query && setShowSuggestions(true)}
                placeholder="Search articles, tags, topics..."
                className="w-full pl-12 pr-20 h-12 text-lg bg-muted/30 border-border"
                data-testid="search-page-input"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {query && (
                  <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0"
                    onClick={() => { setQuery(''); setSuggestions([]); inputRef.current?.focus(); }}
                    data-testid="search-clear-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
                <Button type="button" variant="ghost" size="sm"
                  className={`h-8 w-8 p-0 ${showFilters ? 'text-accent' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                  data-testid="search-filter-toggle"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div ref={suggestionsRef}
                className="absolute z-50 w-full mt-1 bg-paper border border-border rounded-lg shadow-lg overflow-hidden"
                data-testid="search-suggestions"
              >
                {suggestions.map((s, i) => (
                  <button key={`${s.type}-${s.text}-${s.article_id || i}`} type="button"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => handleSuggestionClick(s)}
                    data-testid={`suggestion-${i}`}
                  >
                    {s.type === 'tag' ? (
                      <Tag className="w-4 h-4 text-accent shrink-0" />
                    ) : (
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="truncate">{highlightMatch(s.text, query)}</span>
                    {s.type === 'tag' && s.count && (
                      <span className="ml-auto text-xs text-muted-foreground shrink-0">{s.count} articles</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </form>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-muted/20 border border-border rounded-lg space-y-4" data-testid="search-filters">
              {/* Sort */}
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                  <ArrowUpDown className="w-3 h-3" /> Sort by
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'relevance', label: 'Relevance' },
                    { value: 'date', label: 'Latest' },
                    { value: 'claps', label: 'Most clapped' }
                  ].map(opt => (
                    <Button key={opt.value} variant={sortBy === opt.value ? 'default' : 'outline'} size="sm"
                      className="rounded-full text-xs h-7"
                      onClick={() => handleSortChange(opt.value)}
                      data-testid={`sort-${opt.value}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
              {/* Tag filter */}
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Filter by tag
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {popularTags.map(t => (
                    <Badge key={t.name}
                      variant={selectedTag === t.name ? 'default' : 'secondary'}
                      className="cursor-pointer rounded-full text-xs"
                      onClick={() => handleTagFilter(t.name)}
                      data-testid={`filter-tag-${t.name}`}
                    >
                      {t.name}
                      {selectedTag === t.name && <X className="w-3 h-3 ml-1" />}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Active Filters */}
        {(selectedTag || sortBy !== 'relevance') && activeQuery && (
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="active-filters">
            {selectedTag && (
              <Badge variant="outline" className="rounded-full gap-1 cursor-pointer" onClick={() => handleTagFilter(selectedTag)}>
                Tag: {selectedTag} <X className="w-3 h-3" />
              </Badge>
            )}
            {sortBy !== 'relevance' && (
              <Badge variant="outline" className="rounded-full gap-1 cursor-pointer" onClick={() => handleSortChange('relevance')}>
                Sort: {sortBy} <X className="w-3 h-3" />
              </Badge>
            )}
          </div>
        )}

        {/* Results Count */}
        {activeQuery && (
          <div className="mb-6" data-testid="search-results-count">
            <p className="text-muted-foreground">
              {loading ? 'Searching...' : (
                <>
                  <span className="font-medium text-foreground">{articles.length}</span>
                  {` result${articles.length !== 1 ? 's' : ''} for `}
                  <span className="font-medium text-foreground">"{activeQuery}"</span>
                  {selectedTag && <span> in <span className="text-accent">#{selectedTag}</span></span>}
                </>
              )}
            </p>
          </div>
        )}

        {/* Results */}
        <div className="divide-y divide-border stagger-children" data-testid="search-results">
          {loading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="py-6">
                <div className="flex gap-6">
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-3" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4 mb-3" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                  <Skeleton className="hidden sm:block w-32 h-32 rounded-lg" />
                </div>
              </div>
            ))
          ) : articles.length > 0 ? (
            articles.map(article => (
              <div key={article.article_id}>
                {/* Match indicator */}
                {article.search_match && (
                  <div className="pt-4 flex gap-1">
                    {article.search_match.map(m => (
                      <span key={m} className="text-[10px] uppercase tracking-wider text-accent/70 font-medium">
                        matched in {m}
                      </span>
                    ))}
                  </div>
                )}
                <ArticleCard article={article} onBookmarkChange={handleBookmarkChange} />
              </div>
            ))
          ) : activeQuery ? (
            <div className="py-16 text-center" data-testid="no-results">
              <p className="text-lg text-muted-foreground mb-2">
                No articles found for "{activeQuery}"
              </p>
              <p className="text-sm text-muted-foreground">
                Try a different search term or remove filters.
              </p>
            </div>
          ) : (
            <div className="py-16 text-center">
              <p className="text-lg text-muted-foreground">
                Enter a search term to find articles.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SearchPage;
